import React                   from 'react';
import { WithStyle }           from 'rewire-ui';
import { Observe }             from 'rewire-core';
import LinearProgress          from '@material-ui/core/LinearProgress';
import { Theme }               from '@material-ui/core/styles';
import Typography              from '@material-ui/core/Typography';
import ClearAllIcon            from '@material-ui/icons/ClearAll';
import CloseIcon               from '@material-ui/icons/Close';
import DoneIcon                from '@material-ui/icons/Done';
import MailOutlineIcon         from '@material-ui/icons/MailOutline';
import { history }             from '../RouterWithHistory';
import Dialog                  from '../ui/Dialog2';
import confirmationModel       from './ConfirmationModel';
import { withStyles }          from '../utility/styles';

export type ConfirmationType = 'success' | 'failure' | 'email' | 'custom';

const styles = (theme: Theme) => ({
  confirmationContainer: {
    margin: '0px 16px',
    minHeight: '250px',
    display: 'flex',
    flexDirection: 'column',
  },
  confirmationMessage: {
    fontStyle: 'italic',
  },
  contentContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentInnerContainer: {
    position: 'relative',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '4px',
    '& > a': {
      color: theme.palette.dialogPrimary.main,
      '&:hover': {
        color: theme.palette.dialogPrimary.light,
      },
    },
  },
  progressBar: {
    marginTop: '5px',
  },
  doneIcon: {
    fontSize: '50px',
    color: theme.palette.success.main,
  },
  closeIcon: {
    fontSize: '50px',
    color: theme.palette.error.main,
  },
  emailIcon: {
    fontSize: '40px',
  },
  clearAllIcon: {
    position: 'relative',
    bottom: '3px',
    left: '4px',
    fontSize: '30px',
  },
  emailContainer: {
    position: 'relative',
    right: '5px',
  },
});

interface IConfirmationProps {
  message?:         string | JSX.Element;
  title?:           string | JSX.Element;
  type?:            ConfirmationType;
  redirectTo?:      string;
  redirectTimeout?: number; // milliseconds
  children?:        JSX.Element;
  classes?:         any;
}

type ConfirmationProps = WithStyle<ReturnType<typeof styles>, IConfirmationProps>;

class Confirmation extends React.PureComponent<ConfirmationProps> {
  timeout: any;
  timer:   any;
  timer2:  any;

  constructor(props: ConfirmationProps) {
    super(props);

    confirmationModel.load(props.redirectTimeout);
  }

  componentDidMount() {
    if (this.props.redirectTo) {
      this.timeout = setTimeout(() => {
        history.push(this.props.redirectTo!);
      }, confirmationModel.redirectTimeout);
      this.timer  = setInterval(this.progress, 999);
      this.timer2 = setInterval(this.progress2, 470);
    }
  }

  componentWillUnmount() {
    if (this.props.redirectTo) {
      clearTimeout(this.timeout);
      clearInterval(this.timer);
      clearInterval(this.timer2);
    }
  }

  progress = () => {
    confirmationModel.decrementCountdown();
  };

  progress2 = () => {
    confirmationModel.incrementProgress();
  };

  renderHeader(): JSX.Element | null {
    const {classes, message} = this.props;
    if (!message) {
      return null;
    }

    return (
      typeof message === 'string'
        ? <Typography className={classes.confirmationMessage} variant='subtitle1'>{message}</Typography>
        : message
    );
  }

  renderInnerContent(): JSX.Element {
    const {classes} = this.props;

    switch (this.props.type) {
      case 'success':
        return <DoneIcon className={classes.doneIcon} />;

      case 'failure':
        return <CloseIcon className={classes.closeIcon} />;

      case 'email':
        return (
          <div className={classes.emailContainer}>
            <ClearAllIcon className={classes.clearAllIcon} />
            <MailOutlineIcon className={classes.emailIcon} />
          </div>
        );

      default:
        return this.props.children!;
    }
  }

  renderDialogContents = React.memo((): JSX.Element => {
    const {classes, redirectTo} = this.props;

    return <Observe render={() => (
      <div className={classes.confirmationContainer}>
        {this.renderHeader()}
        <div className={classes.contentContainer}>
          <div className={classes.contentInnerContainer}>
            {this.renderInnerContent()}
          </div>
        </div>
        <div className={classes.progressContainer}>
          {redirectTo &&
            < >
            <span>Redirection in {confirmationModel.countdown} seconds</span>
            <LinearProgress className={classes.progressBar} variant='determinate' value={confirmationModel.progress} />
            </>
          }
        </div>
      </div>
    )} />;
  });

  render() {
    const {title} = this.props;
    return <Observe render={() => (
      <Dialog dialog={confirmationModel} title={() => <span>{title ?? ''}</span>} hideBackdrop={true} disableEscapeKeyDown={true} maxWidth='xs' disableTransition={true}>
        <this.renderDialogContents />
      </Dialog>
    )} />;
  }
}

export default withStyles<IConfirmationProps>(styles, Confirmation);
